import React, { useState, useMemo, useCallback } from "react";
import { MenuItem } from "@mui/material";
import { FaNotesMedical } from "react-icons/fa";
import { COLORS } from "../../styles/uiConstants";
import useSystemVariable from "./shared/useSystemVariable";
import SystemVariablePage from "./shared/SystemVariablePage";
import { FormSelect } from "../../components/atoms";
import { normalizeHeader, pickCell, downloadExcelTemplate } from "./shared/excelUtils";
import {
  useListDiagnosesMutation, useCreateDiagnosisMutation,
  useUpdateDiagnosisMutation, useDeleteDiagnosisMutation,
  useBulkUpsertDiagnosesMutation, useListDiagnosisCategoriesMutation,
} from "../../store/systemVariablesApi";

const mapExcelRow = (row) => {
  const n = {};
  Object.keys(row || {}).forEach((k) => { n[normalizeHeader(k)] = row[k]; });
  return {
    categoryName: (pickCell(n, ["tanı kategorisi", "tani kategorisi", "kategori", "category"]) || "").toString().trim(),
    code: (pickCell(n, ["tanı kodu", "tani kodu", "kod", "code"]) || "").toString().trim(),
    name: (pickCell(n, ["tanı", "tani", "tanı adı", "tani adi", "ad", "name"]) || "").toString().trim(),
  };
};

const TEMPLATE_ROWS = [
  { "Tanı Kategorisi": "Endokrinoloji", "Tanı Kodu": "E11", Tanı: "Tip 2 Diabetes Mellitus" },
  { "Tanı Kategorisi": "Kardiyoloji", "Tanı Kodu": "I10", Tanı: "Esansiyel hipertansiyon" },
  { "Tanı Kategorisi": "Gastroenteroloji", "Tanı Kodu": "K21", Tanı: "Gastroözofageal reflü" },
  { "Tanı Kategorisi": "Nöroloji", "Tanı Kodu": "G43", Tanı: "Migren" },
];

const TABLE_COLUMNS = [
  { key: "code", label: "Tanı Kodu", width: 140, bold: true },
  { key: "name", label: "Tanı", bold: true },
  { key: "category", label: "Kategori", width: 240, render: (d) => d?.category?.name || "-" },
];

const FORM_FIELDS = [
  { key: "code", label: "Tanı Kodu", required: true },
  { key: "name", label: "Tanı", required: true },
  { key: "categoryId", label: "Kategori" },
];

const IMPORT_COLUMNS = [
  { key: "categoryName", label: "Tanı Kategorisi", bold: true },
  { key: "code", label: "Tanı Kodu", bold: true },
  { key: "name", label: "Tanı" },
];

export default function Diagnoses() {
  const [catItems, setCatItems] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState("");
  const [listCategories] = useListDiagnosisCategoriesMutation();

  const fetchCategories = useCallback(async (token) => {
    try {
      const res = await listCategories({ token, status: "all", page: 1, limit: 500 }).unwrap();
      setCatItems(res?.data?.categories || []);
    } catch {}
  }, [listCategories]);

  const vm = useSystemVariable({
    listMutation: useListDiagnosesMutation(),
    createMutation: useCreateDiagnosisMutation(),
    updateMutation: useUpdateDiagnosisMutation(),
    deleteMutation: useDeleteDiagnosisMutation(),
    bulkMutation: useBulkUpsertDiagnosesMutation(),
    itemsKey: "diagnoses",
    idKey: "diagnosisId",
    defaultForm: { code: "", name: "", categoryId: "" },
    itemToForm: (dx) => ({ code: dx?.code || "", name: dx?.name || "", categoryId: dx?.category?._id || dx?.category || "" }),
    formToPayload: (f) => ({ code: f.code, name: f.name, categoryId: f.categoryId }),
    onAfterBoot: fetchCategories,
    entityLabel: "Tanı",
    extraParams: { categoryId: categoryFilter || undefined },
  });

  const canSubmit = useMemo(() => Boolean(vm.form.code?.trim() && vm.form.name?.trim() && vm.form.categoryId), [vm.form]);

  const formSections = [
    { title: "Tanı Bilgisi", fields: [
      { key: "code", label: "Tanı Kodu", required: true },
      { key: "name", label: "Tanı", required: true },
    ]},
    { title: "Kategori", fields: [
      { key: "categoryId", label: "Tanı Kategorisi", required: true,
        // InfoTile içinde çizilir — etiket tile'ın kendi başlığı, o yüzden
        // standard variant + underline'sız (diğer drawer alanlarıyla aynı dil).
        render: (form, setField) => (
          <FormSelect
            variant="standard"
            value={form.categoryId || ""}
            onChange={(v) => setField("categoryId", v)}
            placeholder={null}
            InputProps={{ disableUnderline: true }}
            inputProps={{ "aria-label": "Tanı Kategorisi" }}
            SelectProps={{
              displayEmpty: true,
              renderValue: (val) => {
                if (!val) return "Kategori seçin";
                return catItems.find((c) => c._id === val)?.name || val;
              },
            }}
            sx={{
              "& .MuiInputBase-root": {
                fontSize: "0.875rem",
                fontWeight: 600,
                color: form.categoryId ? COLORS.slate800 : COLORS.slate400,
              },
              "& .MuiSelect-select": { py: 0 },
            }}
          >
            {catItems.filter((c) => c.isActive).map((c) => <MenuItem key={c._id} value={c._id}>{c.name}</MenuItem>)}
          </FormSelect>
        ),
      },
    ]},
  ];

  const extraFilters = [
    {
      key: "category",
      type: "select",
      placeholder: "Kategori",
      options: [{ value: "", label: "Tümü" }, ...catItems.map((c) => ({ value: c._id, label: c.name }))],
      value: categoryFilter,
      onChange: (v) => { setCategoryFilter(v); vm.setPage(1); },
    },
  ];

  return (
    <SystemVariablePage
      icon={<FaNotesMedical size={22} />}
      title="Tanı Şablonları"
      subtitle={<>Tanı listesi. Alanlar: <b>Tanı Kodu</b>, <b>Tanı</b>, <b>Kategori</b></>}
      entityLabel="Tanı"
      vm={{ ...vm, canSubmit }}
      tableColumns={TABLE_COLUMNS}
      formFields={FORM_FIELDS}
      formSections={formSections}
      importColumns={IMPORT_COLUMNS}
      mapExcelRow={(row) => { const r = mapExcelRow(row); return r.categoryName && r.code && r.name ? r : { name: "" }; }}
      downloadTemplate={() => downloadExcelTemplate(TEMPLATE_ROWS, "Tanılar", "tani_sablonu.xlsx")}
      extraFilters={extraFilters}
    />
  );
}
